
import React, { useState } from "react";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Trash2, Plus, BookOpen } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { DocumentUploadForm } from "./document-upload-form";
import { KnowledgeSummaryForm } from "./knowledge-summary-form";
import { Badge } from "@/components/ui/badge";

interface DocumentMetadata {
  title: string;
  category: string;
  description?: string;
}

interface KnowledgeSummaryData {
  title: string;
  category: string;
  content: string;
  description?: string;
}

export const DocumentManagement = () => {
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmittingSummary, setIsSubmittingSummary] = useState(false);

  const queryClient = useQueryClient();

  const { data: documents, refetch } = useQuery({
    queryKey: ["knowledge_documents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("knowledge_documents")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching documents:", error);
        return [];
      }
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("knowledge_documents")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Error deleting document:", error);
        throw new Error("Failed to delete document");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["knowledge_documents"] });
      toast({
        title: "Document deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete document",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from("knowledge_documents")
        .update({ is_active: !is_active })
        .eq("id", id);

      if (error) {
        console.error("Error toggling document status:", error);
        throw new Error("Failed to toggle document status");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["knowledge_documents"] });
      toast({
        title: "Document status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update document status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const uploadDocument = async (file: File, metadata: DocumentMetadata) => {
    setIsUploading(true);
    
    try {
      console.log(`📄 UPLOAD START: ${file.name}`);
      
      // Enhanced file validation
      const supportedTypes = [
        'application/pdf',
        'text/plain',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      const supportedExtensions = ['.pdf', '.txt', '.md', '.csv', '.docx'];
      const hasValidType = supportedTypes.includes(file.type) || 
                          supportedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!hasValidType) {
        throw new Error(`Unsupported file type: ${file.type}. Supported: ${supportedExtensions.join(', ')}`);
      }

      // Check file size limits - reduced to 100KB for reliability
      const maxSize = 100 * 1024; // 100KB
      if (file.size > maxSize) {
        throw new Error(`File too large: ${(file.size / 1024).toFixed(1)}KB. Max: 100KB. Use "Text Summary" for larger documents.`);
      }

      console.log(`✅ File validation passed`);

      // Create document record
      const { data: document, error: docError } = await supabase
        .from('knowledge_documents')
        .insert({
          title: metadata.title,
          category: metadata.category,
          file_type: file.type || 'application/octet-stream',
          original_filename: file.name,
          file_size_bytes: file.size,
          processing_status: 'pending',
          uploaded_by: null,
          content: ''
        })
        .select()
        .single();

      if (docError || !document) {
        throw new Error(`Failed to create document record: ${docError?.message}`);
      }

      // Prepare FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentId', document.id);

      // Call Edge Function with shorter timeout
      const { data: extractionResult, error: extractionError } = await supabase.functions.invoke('universal-document-extractor', {
        body: formData
      });

      if (extractionError) {
        console.error('Edge Function error:', extractionError);
        
        await supabase
          .from('knowledge_documents')
          .update({ 
            processing_status: 'failed',
            extraction_metadata: {
              error: extractionError.message,
              failedAt: new Date().toISOString()
            }
          })
          .eq('id', document.id);
          
        throw new Error(`Document processing failed: ${extractionError.message}`);
      }

      if (!extractionResult?.success) {
        throw new Error(extractionResult?.error || 'Unknown extraction error');
      }

      toast({
        title: "Document uploaded successfully",
        description: `Created ${extractionResult.chunksCreated} chunks from ${file.name}`,
      });

      await refetch();
      setIsUploadDialogOpen(false);
      
    } catch (error: any) {
      console.error('Upload failed:', error);
      
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const submitSummary = async (summaryData: KnowledgeSummaryData) => {
    setIsSubmittingSummary(true);
    
    try {
      console.log('📝 SUMMARY SUBMIT START:', summaryData.title);
      
      // Create document record with content directly
      const { data: document, error: docError } = await supabase
        .from('knowledge_documents')
        .insert({
          title: summaryData.title,
          category: summaryData.category,
          file_type: 'text/plain',
          original_filename: null,
          file_size_bytes: summaryData.content.length,
          processing_status: 'pending',
          uploaded_by: null,
          content: summaryData.content
        })
        .select()
        .single();

      if (docError || !document) {
        throw new Error(`Failed to create document: ${docError?.message}`);
      }

      // Generate embedding directly via semantic search function
      const { data: embeddingResult, error: embeddingError } = await supabase.functions.invoke('semantic-search', {
        body: { 
          query: summaryData.content.substring(0, 1000), // Use first 1000 chars for embedding
          action: 'create_embedding',
          documentId: document.id,
          fullContent: summaryData.content
        }
      });

      if (embeddingError) {
        console.warn('Embedding creation failed, but document saved:', embeddingError);
      }

      // Update status to completed
      await supabase
        .from('knowledge_documents')
        .update({ 
          processing_status: 'completed',
          chunk_count: 1,
          extraction_metadata: {
            method: 'manual_summary',
            submittedAt: new Date().toISOString(),
            characterCount: summaryData.content.length
          }
        })
        .eq('id', document.id);

      toast({
        title: "Sammendrag lagret",
        description: `"${summaryData.title}" ble lagret og er klar for semantisk søk`,
      });

      await refetch();
      setIsSummaryDialogOpen(false);
      
    } catch (error: any) {
      console.error('Summary submission failed:', error);
      
      toast({
        title: "Lagring feilet",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmittingSummary(false);
    }
  };

  const deleteDocument = async (id: string) => {
    deleteMutation.mutate(id);
  };

  const toggleDocumentStatus = async (id: string, is_active: boolean) => {
    toggleMutation.mutate({ id, is_active });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Document Management</h2>
          <p className="text-muted-foreground">
            Upload small files or create text summaries for large documents.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isSummaryDialogOpen} onOpenChange={setIsSummaryDialogOpen}>
            <DialogTrigger asChild>
              <Button disabled={isSubmittingSummary}>
                <BookOpen className="mr-2 h-4 w-4" />
                {isSubmittingSummary ? 'Lagrer...' : 'Tekst-sammendrag'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Opprett tekst-sammendrag</DialogTitle>
                <DialogDescription>
                  Perfekt for store rapporter og dokumenter. Lim inn eller skriv et sammendrag på opptil ~10 sider.
                </DialogDescription>
              </DialogHeader>
              <KnowledgeSummaryForm 
                onSubmit={submitSummary}
                isSubmitting={isSubmittingSummary}
                onCancel={() => setIsSummaryDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={isUploading}>
                <Upload className="mr-2 h-4 w-4" />
                {isUploading ? 'Prosesserer...' : 'Last opp fil'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Last opp liten fil</DialogTitle>
                <DialogDescription>
                  For filer under 100KB. Større dokumenter bør legges inn som tekst-sammendrag.
                </DialogDescription>
              </DialogHeader>
              <DocumentUploadForm 
                onUpload={uploadDocument}
                isUploading={isUploading}
                onCancel={() => setIsUploadDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Display documents */}
      <div className="grid gap-4">
        {documents?.map((doc) => (
          <Card key={doc.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-semibold">{doc.title}</h3>
                    <Badge variant="outline">
                      {getFileTypeDisplay(doc.file_type, doc.original_filename)}
                    </Badge>
                    {!doc.original_filename && (
                      <Badge variant="secondary">Sammendrag</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Kategori: {doc.category}</span>
                    <span>Chunks: {doc.chunk_count || 0}</span>
                    <span>Størrelse: {formatFileSize(doc.file_size_bytes || 0)}</span>
                    {doc.total_pages && doc.total_pages > 0 && (
                      <span>Sider: {doc.total_pages}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={doc.processing_status === 'completed' ? 'default' : 
                              doc.processing_status === 'failed' ? 'destructive' : 'secondary'}
                    >
                      {doc.processing_status}
                    </Badge>
                    {doc.extraction_metadata && 
                     typeof doc.extraction_metadata === 'object' && 
                     doc.extraction_metadata !== null &&
                     'method' in doc.extraction_metadata && (
                      <Badge variant="outline">
                        {doc.extraction_metadata.method === 'manual_summary' ? 'Manuelt sammendrag' : String(doc.extraction_metadata.method)}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleDocumentStatus(doc.id, doc.is_active)}
                    disabled={toggleMutation.isPending}
                  >
                    {doc.is_active ? 'Deaktiver' : 'Aktiver'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => deleteDocument(doc.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Helper function to display file type
function getFileTypeDisplay(fileType: string, filename?: string): string {
  if (filename) {
    const extension = filename.split('.').pop()?.toUpperCase();
    if (extension) return extension;
  }
  
  const typeMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX'
  };
  
  return typeMap[fileType] || 'TXT';
}
